import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from './icons';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Admin management for the key-redemption / account-distribution system.
// This is separate from Pre-Made Keys. Use this page to define redemption
// products, add stock accounts, and generate redemption keys. If you want to
// sell those keys through Pre-Made Keys, import them manually there.
function KeyRedemptionAdmin() {
    const [sub, setSub] = useState('cards');
    const [stats, setStats] = useState(null);
    const [products, setProducts] = useState([]);
    const [productNames, setProductNames] = useState([]);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [pendingConfirm, setPendingConfirm] = useState(null);

    // Cards
    const [cards, setCards] = useState([]);
    const [cardPage, setCardPage] = useState(1);
    const [cardPages, setCardPages] = useState(1);
    const [cardFilters, setCardFilters] = useState({ status: 'all', product: 'all', keyword: '' });
    const [invalidCounts, setInvalidCounts] = useState({ banned: 0, expired: 0 });   // for the "Delete all invalid" button
    const [gen, setGen] = useState({ productName: '', count: 10, durationMonths: 1 });
    const [generatedKeys, setGeneratedKeys] = useState(null);
    const [pricing, setPricing] = useState({});
    const [pricingDraft, setPricingDraft] = useState({});

    // Redemption-page settings (shared with legacy config)
    const [settings, setSettings] = useState({ redeemUrl: '', description: '', redeemTutorial: '' });
    // Operation Tutorial editor: which product (or the global default) and its draft text.
    const [tutorialTarget, setTutorialTarget] = useState('__default__');
    const [tutorialDraft, setTutorialDraft] = useState('');

    // Accounts
    const [accounts, setAccounts] = useState([]);
    const [accPage, setAccPage] = useState(1);
    const [accPages, setAccPages] = useState(1);
    const [accFilters, setAccFilters] = useState({ product: 'all', keyword: '', status: 'all', notes: '' });
    const [noteLabels, setNoteLabels] = useState([]);
    const [accountTotal, setAccountTotal] = useState(0);
    const [notesEditor, setNotesEditor] = useState(null);
    const [exportingAccounts, setExportingAccounts] = useState(false);
    const [exportBeforeDelete, setExportBeforeDelete] = useState(true);
    const [invalidAccounts, setInvalidAccounts] = useState({ total: 0, deletable: 0, keptActive: 0 });   // for "Delete all invalid"
    const [addAcc, setAddAcc] = useState({ productName: '', accounts: '', notes: '' });
    // Verification code fetched on demand for a stock account, keyed by account
    // id — works even for never-redeemed accounts (no card/key involved).
    const [otpLookup, setOtpLookup] = useState({});
    // Mailbox connectivity test result, keyed by account id.
    const [otpTest, setOtpTest] = useState({});
    // Switch behaviour: may "Change account" fall back to an account with no
    // working OTP mailbox when no tokened account is free?
    const [allowSwitchWithoutMailbox, setAllowSwitchWithoutMailbox] = useState(false);
    // Deliberately undocumented: the button only fires on the 4th click within
    // a short window, so it isn't obvious from just clicking it once.
    const otpClickTracker = useRef({});

    // Products
    const [newProduct, setNewProduct] = useState({ name: '', creditPrice: 0, durationDays: 30, refreshInterval: 14 });
    const [editProduct, setEditProduct] = useState(null);
    const [otpProduct, setOtpProduct] = useState(null);
    const [otpForm, setOtpForm] = useState({
        otpEnabled: true,
        otpSenderKeywords: '',
        otpSubjectKeywords: '',
        otpCodePatterns: '',
        otpFreshMinutes: 10,
    });

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, []);

    const flash = (msg, isError = false) => {
        if (isError) { setError(msg); setNotice(null); } else { setNotice(msg); setError(null); }
        setTimeout(() => { setError(null); setNotice(null); }, 4000);
    };

    const loadStats = useCallback(async () => {
        const { body } = await authFetch('/admin/redeem/stats');
        if (body.success) setStats(body.stats);
    }, [authFetch]);

    const loadProducts = useCallback(async () => {
        const { body } = await authFetch('/admin/redeem/products');
        if (body.success) { setProducts(body.products); setProductNames(body.productNames); }
    }, [authFetch]);

    const loadCards = useCallback(async () => {
        const q = new URLSearchParams({
            page: cardPage, status: cardFilters.status, product: cardFilters.product, keyword: cardFilters.keyword,
        });
        const { body } = await authFetch(`/admin/redeem/cards?${q}`);
        if (body.success) { setCards(body.cards); setCardPages(body.pagination.totalPages); setInvalidCounts(body.invalid || { banned: 0, expired: 0 }); }
    }, [authFetch, cardPage, cardFilters]);

    const loadAccounts = useCallback(async () => {
        const q = new URLSearchParams({ page: accPage, ...accFilters });
        const { body } = await authFetch(`/admin/redeem/accounts?${q}`);
        if (body.success) { setAccounts(body.accounts); setAccPages(body.pagination.totalPages); setAccountTotal(body.pagination.total); setNoteLabels(body.noteLabels || []); setInvalidAccounts(body.invalid || { total: 0, deletable: 0, keptActive: 0 }); }
    }, [authFetch, accPage, accFilters]);

    const loadPricing = useCallback(async () => {
        const { body } = await authFetch('/admin/redeem/pricing');
        if (body.success) { setPricing(body.pricing || {}); setPricingDraft(body.pricing || {}); }
    }, [authFetch]);

    const loadSettings = useCallback(async () => {
        const { body } = await authFetch('/admin/redeem/settings');
        if (body.success) setSettings({ redeemUrl: body.redeemUrl || '', description: body.description || '', redeemTutorial: body.redeemTutorial || '' });
    }, [authFetch]);

    const saveSettings = async () => {
        const { ok, body } = await authFetch('/admin/redeem/settings', { method: 'PUT', body: JSON.stringify(settings) });
        if (ok && body.success) { setSettings({ redeemUrl: body.redeemUrl || '', description: body.description || '', redeemTutorial: body.redeemTutorial || '' }); flash('Redemption page settings saved'); }
        else flash(body.error || 'Failed to save settings', true);
    };

    // Load the draft text for the currently-selected tutorial target.
    const tutorialTextFor = useCallback((target) => {
        if (target === '__default__') return settings.redeemTutorial || '';
        const p = products.find(pr => String(pr.id) === String(target));
        return (p && p.tutorial) || '';
    }, [settings.redeemTutorial, products]);

    const selectTutorialTarget = (target) => {
        setTutorialTarget(target);
        setTutorialDraft(tutorialTextFor(target));
    };

    const saveTutorial = async () => {
        if (tutorialTarget === '__default__') {
            const { ok, body } = await authFetch('/admin/redeem/settings', { method: 'PUT', body: JSON.stringify({ ...settings, redeemTutorial: tutorialDraft }) });
            if (ok && body.success) { setSettings({ redeemUrl: body.redeemUrl || '', description: body.description || '', redeemTutorial: body.redeemTutorial || '' }); flash('Default tutorial saved'); }
            else flash(body.error || 'Failed to save tutorial', true);
        } else {
            const { ok, body } = await authFetch(`/admin/redeem/products/${tutorialTarget}/tutorial`, { method: 'PUT', body: JSON.stringify({ tutorial: tutorialDraft }) });
            if (ok && body.success) { await loadProducts(); flash('Product tutorial saved'); }
            else flash(body.error || 'Failed to save tutorial', true);
        }
    };

    // Defined before the effect below that depends on it — a `const` referenced
    // in a dependency array is evaluated during render, so declaring it later
    // would throw a temporal-dead-zone ReferenceError and blank the page.
    const loadSwitchSettings = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/redeem/switch-settings');
        if (ok && body.success) setAllowSwitchWithoutMailbox(body.settings?.allowSwitchWithoutMailbox === true);
    }, [authFetch]);

    useEffect(() => { loadStats(); loadProducts(); }, [loadStats, loadProducts]);
    useEffect(() => { if (sub === 'cards') loadCards(); }, [sub, loadCards]);
    useEffect(() => { if (sub === 'accounts') { loadAccounts(); loadSwitchSettings(); } }, [sub, loadAccounts, loadSwitchSettings]);
    useEffect(() => { if (sub === 'tutorial') loadSettings(); }, [sub, loadSettings]);
    // Keep the draft in sync when settings/products (re)load for the current target.
    useEffect(() => { setTutorialDraft(tutorialTextFor(tutorialTarget)); }, [tutorialTextFor, tutorialTarget]);

    const generate = async () => {
        if (!gen.productName) return flash('Choose a product first', true);
        const { ok, body } = await authFetch('/admin/redeem/cards/generate', { method: 'POST', body: JSON.stringify(gen) });
        if (ok && body.success) {
            setGeneratedKeys(body.keys);
            flash(`Generated ${body.keys.length} key(s)`);
            loadCards(); loadStats(); loadProducts();
        } else flash(body.error || 'Failed to generate', true);
    };

    const savePricing = async () => {
        const { ok, body } = await authFetch('/admin/redeem/pricing', { method: 'PUT', body: JSON.stringify({ pricing: pricingDraft }) });
        if (ok && body.success) { setPricing(body.pricing || {}); setPricingDraft(body.pricing || {}); flash('Pricing saved'); }
        else flash(body.error || 'Failed to save pricing', true);
    };

    const runCardAction = async (key, action) => {
        const { ok, body } = await authFetch('/admin/redeem/cards/action', { method: 'POST', body: JSON.stringify({ key, action }) });
        if (ok && body.success) { loadCards(); loadStats(); } else flash(body.error || 'Action failed', true);
    };

    // One click: delete every banned + expired key (of the selected product).
    const deleteInvalid = () => {
        const total = invalidCounts.banned + invalidCounts.expired;
        if (!total) return;
        const scope = cardFilters.product !== 'all' ? ` of "${cardFilters.product}"` : '';
        setPendingConfirm({
            title: 'Delete all invalid keys',
            message: `Delete ${total} invalid key${total === 1 ? '' : 's'}${scope}: ${invalidCounts.banned} banned and ${invalidCounts.expired} expired? `
                + 'An account still attached to a banned key goes back to stock. This cannot be undone.',
            confirmLabel: `Delete ${total}`,
            onConfirm: async () => {
                const { ok, body } = await authFetch('/admin/redeem/cards/delete-invalid', {
                    method: 'POST', body: JSON.stringify({ product: cardFilters.product }),
                });
                if (ok && body.success) {
                    flash(`Deleted ${body.deleted} invalid key${body.deleted === 1 ? '' : 's'} (${body.banned} banned, ${body.expired} expired).`
                        + (body.accountsReturned ? ` ${body.accountsReturned} account${body.accountsReturned === 1 ? '' : 's'} returned to stock.` : ''));
                    setCardPage(1); loadCards(); loadStats();
                } else flash(body.error || 'Could not delete the invalid keys', true);
            },
        });
    };

    const cardAction = async (key, action) => {
        if (action === 'delete') {
            setPendingConfirm({
                title: 'Delete Key',
                message: 'Delete this key? Its account will be returned to stock.',
                confirmLabel: 'Delete',
                onConfirm: () => runCardAction(key, action),
            });
            return;
        }
        runCardAction(key, action);
    };

    const addAccounts = async () => {
        if (!addAcc.productName) return flash('Choose a product first', true);
        const { ok, body } = await authFetch('/admin/redeem/accounts', { method: 'POST', body: JSON.stringify(addAcc) });
        if (ok && body.success) {
            flash(`Added ${body.added} account(s)${body.skipped ? `, skipped ${body.skipped} duplicate(s)` : ''}${body.mailboxes ? `, linked ${body.mailboxes} OTP mailbox(es)` : ''}`);
            setAddAcc({ ...addAcc, accounts: '' });
            loadAccounts(); loadStats(); loadProducts();
        } else flash(body.error || 'Failed to add accounts', true);
    };

    const saveSwitchSettings = async (value) => {
        setAllowSwitchWithoutMailbox(value); // optimistic
        const { ok, body } = await authFetch('/admin/redeem/switch-settings', {
            method: 'PUT',
            body: JSON.stringify({ allowSwitchWithoutMailbox: value }),
        });
        if (ok && body.success) {
            flash(value
                ? 'Customers may now switch to accounts without a working mailbox when tokened stock runs out.'
                : 'Switching is now restricted to accounts with a working mailbox.');
        } else {
            setAllowSwitchWithoutMailbox(!value); // revert
            flash(body.error || 'Failed to save setting', true);
        }
    };

    const testAccountMailbox = async (id) => {
        setOtpTest((prev) => ({ ...prev, [id]: { status: 'loading' } }));
        const { ok, body } = await authFetch(`/admin/redeem/accounts/${id}/test-otp`, { method: 'POST' });
        setOtpTest((prev) => ({ ...prev, [id]: { status: ok && body.success ? 'success' : 'error', message: body.message || (body.success ? 'OK' : 'Failed') } }));
        loadAccounts();
    };

    // Negative values are day plans; 0 = Any/shared stock; positive = months.
    const durationLabel = (m) => (Number(m) === 0 ? 'Any' : Number(m) < 0 ? `${-Number(m)} day${Number(m) === -1 ? '' : 's'}` : `${m} mo`);
    // Duration options offered when generating keys / reserving stock.
    const DURATION_OPTIONS = [-1, -7, -14, ...Array.from({ length: 12 }, (_, i) => i + 1)];

    const runAccountAction = async (id, action) => {
        const { ok, body } = await authFetch('/admin/redeem/accounts/action', { method: 'POST', body: JSON.stringify({ id, action }) });
        if (ok && body.success) { loadAccounts(); loadStats(); } else flash(body.error || 'Action failed', true);
    };

    const fetchAccountOtp = async (id) => {
        setOtpLookup((prev) => ({ ...prev, [id]: { status: 'loading' } }));
        const { ok, body } = await authFetch(`/admin/redeem/accounts/${id}/get-code`, { method: 'POST' });
        if (ok && body.success) {
            setOtpLookup((prev) => ({ ...prev, [id]: { status: 'success', code: body.code } }));
        } else {
            setOtpLookup((prev) => ({ ...prev, [id]: { status: 'error', message: body.message || 'Failed to fetch code' } }));
        }
    };

    // Requires 4 clicks within 2s of each other to actually trigger the lookup —
    // a single accidental click does nothing and shows no feedback either way.
    const handleOtpButtonClick = (id) => {
        const now = Date.now();
        const entry = otpClickTracker.current[id];
        const count = entry && now - entry.lastClick < 2000 ? entry.count + 1 : 1;
        otpClickTracker.current[id] = { count, lastClick: now };
        if (count >= 4) {
            otpClickTracker.current[id] = { count: 0, lastClick: now };
            fetchAccountOtp(id);
        }
    };

    const runInvalidateSearch = async () => {
        const { ok, body } = await authFetch('/admin/redeem/accounts/invalidate-search', {
            method: 'POST',
            body: JSON.stringify(accFilters),
        });
        if (ok && body.success) {
            flash(`Invalidated ${body.invalidated} account(s). ${body.cardsAffected} customer(s) can now use "Account abnormal? Change account" to get a new one.`);
            loadAccounts(); loadStats();
        } else flash(body.error || 'Could not invalidate accounts', true);
    };

    // One click: delete every invalid stock account (of the selected product).
    const deleteInvalidAccounts = () => {
        const n = invalidAccounts.deletable;
        if (!n) return;
        setExportBeforeDelete(true);
        const scope = accFilters.product !== 'all' ? ` of "${accFilters.product}"` : '';
        setPendingConfirm({
            exportFilters: { ...accFilters, mode: 'invalid' },
            title: 'Delete all invalid accounts',
            message: `Delete ${n.toLocaleString()} invalid account${n === 1 ? '' : 's'}${scope}? `
                + (invalidAccounts.keptActive ? `${invalidAccounts.keptActive.toLocaleString()} invalid account${invalidAccounts.keptActive === 1 ? ' is' : 's are'} kept because a customer's live key still uses ${invalidAccounts.keptActive === 1 ? 'it' : 'them'}, so they can still switch. ` : '')
                + 'Their mailbox login data is removed too. This cannot be undone.',
            confirmLabel: `Delete ${n.toLocaleString()}`,
            onConfirm: async () => {
                const { ok, body } = await authFetch('/admin/redeem/accounts/delete-invalid', {
                    method: 'POST', body: JSON.stringify(accFilters),
                });
                if (ok && body.success) {
                    flash(`Deleted ${body.deleted.toLocaleString()} invalid account${body.deleted === 1 ? '' : 's'}.`
                        + (body.keptActive ? ` ${body.keptActive.toLocaleString()} kept (still used by live keys).` : ''));
                    setAccPage(1); loadAccounts(); loadStats();
                } else flash(body.error || 'Could not delete the invalid accounts', true);
            },
        });
    };

    const invalidateSearch = () => {
        const scope = `${accountTotal.toLocaleString()} accounts in the current filter${accFilters.notes ? ` with notes "${accFilters.notes}"` : ''}, across all pages`;
        setPendingConfirm({
            title: 'Invalidate matching accounts',
            message: `Mark ${scope} as invalid? Their customers will then be able to click "Account abnormal? Change account" to switch to a new valid account. You can re-enable them later if needed.`,
            confirmLabel: 'Invalidate',
            onConfirm: runInvalidateSearch,
        });
    };

    const saveAccountNotes = async () => {
        const { ok, body } = await authFetch(`/admin/redeem/accounts/${notesEditor.id}/notes`, {
            method: 'PUT', body: JSON.stringify({ notes: notesEditor.notes }),
        });
        if (ok && body.success) { setNotesEditor(null); loadAccounts(); flash('Notes saved'); }
        else flash(body.error || 'Could not save notes', true);
    };

    const deleteMatchingAccounts = () => {
        const filters = { ...accFilters };
        setExportBeforeDelete(true);
        setPendingConfirm({
            exportFilters: filters,
            title: 'Delete matching accounts',
            message: `Permanently delete ${accountTotal.toLocaleString()} accounts in the current filter${filters.notes ? ` with notes "${filters.notes}"` : ''}, across all pages? Their stored mailbox credentials and notes will also be removed. Bound keys lose these accounts; customers can use "Change account" to get a replacement. This cannot be undone.`,
            confirmLabel: 'Delete matching',
            onConfirm: async () => {
                const { ok, body } = await authFetch('/admin/redeem/accounts/delete-matching', { method: 'POST', body: JSON.stringify(filters) });
                if (ok && body.success) { setAccPage(1); loadAccounts(); loadStats(); flash(`Deleted ${body.deleted} account(s).`); }
                else flash(body.error || 'Could not delete accounts', true);
            },
        });
    };

    // Keys tab: the stock accounts behind every key in the current filter (all
    // pages), e.g. status "expired" = the retired mailboxes, ready to delete.
    const exportKeyAccounts = () => exportAccounts({ ...cardFilters }, '/admin/redeem/cards/export');

    const exportAccounts = async (filters, path = '/admin/redeem/accounts/export') => {
        setExportingAccounts(true);
        try {
            const response = await fetch(`${API_URL}${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
                body: JSON.stringify(filters),
            });
            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.error || 'Could not export accounts. Nothing was deleted.');
            }
            if (!response.headers.get('Content-Type')?.includes('spreadsheetml.sheet')) throw new Error('The export response was not an Excel workbook. Nothing was deleted.');
            const blob = await response.blob();
            if (!blob.size) throw new Error('The Excel export was empty. Nothing was deleted.');
            const url = URL.createObjectURL(blob), link = document.createElement('a');
            link.href = url;
            link.download = response.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] || 'stock-accounts.xlsx';
            document.body.appendChild(link); link.click(); link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            const missing = Number(response.headers.get('X-Missing-Accounts')) || 0;
            flash(`Excel export downloaded (${response.headers.get('X-Exported-Accounts') || 'matching'} accounts).`
                + (missing ? ` ${missing} key${missing === 1 ? '' : 's'} had an account that was already deleted from stock, so ${missing === 1 ? 'it is' : 'they are'} not in the file.` : ''));
            return true;
        } catch (error) {
            flash(error.message || 'Export failed. Nothing was deleted.', true);
            return false;
        } finally {
            setExportingAccounts(false);
        }
    };

    const accountAction = async (id, action) => {
        if (action === 'delete') {
            setExportBeforeDelete(true);
            setPendingConfirm({
                exportFilters: { accountId: id },
                title: 'Delete Stock Account',
                message: 'Delete this account from stock?',
                confirmLabel: 'Delete',
                onConfirm: () => runAccountAction(id, action),
            });
            return;
        }
        runAccountAction(id, action);
    };

    const addProduct = async () => {
        if (!newProduct.name.trim()) return;
        const { ok, body } = await authFetch('/admin/redeem/products', { method: 'POST', body: JSON.stringify(newProduct) });
        if (ok && body.success) { setNewProduct({ name: '', creditPrice: 0, durationDays: 30, refreshInterval: 14 }); flash('Product added'); loadProducts(); } else flash(body.error || 'Failed', true);
    };

    const openOtpProduct = (p) => {
        setOtpProduct(p);
        setOtpForm({
            otpEnabled: p.otpEnabled !== false,
            otpSenderKeywords: p.otpSenderKeywords || p.otpDefaults?.senderKeywords || '',
            otpSubjectKeywords: p.otpSubjectKeywords || p.otpDefaults?.subjectKeywords || '',
            otpCodePatterns: p.otpCodePatterns || p.otpDefaults?.codePatterns || '',
            otpFreshMinutes: p.otpFreshMinutes || p.otpDefaults?.freshMinutes || 10,
        });
    };

    const useOtpDefaults = () => {
        if (!otpProduct?.otpDefaults) return;
        setOtpForm({
            otpEnabled: true,
            otpSenderKeywords: otpProduct.otpDefaults.senderKeywords || '',
            otpSubjectKeywords: otpProduct.otpDefaults.subjectKeywords || '',
            otpCodePatterns: otpProduct.otpDefaults.codePatterns || '',
            otpFreshMinutes: otpProduct.otpDefaults.freshMinutes || 10,
        });
    };

    const saveProduct = async (p = otpProduct) => {
        if (!p?.id) return;
        const { ok, body } = await authFetch(`/admin/redeem/products/${p.id}`, {
            method: 'PUT',
            body: JSON.stringify(otpForm),
        });
        if (ok && body.success) {
            setEditProduct(null);
            setOtpProduct(body.product || null);
            flash('OTP rules saved');
            loadProducts();
        } else flash(body.error || 'Failed', true);
    };

    const deleteProduct = async (id) => {
        const { ok, body } = await authFetch(`/admin/redeem/products/${id}`, { method: 'DELETE' });
        if (ok && body.success) { flash('Product deleted'); loadProducts(); } else flash(body.error || 'Failed', true);
    };

    const confirmDeleteProduct = (id) => {
        setPendingConfirm({
            title: 'Delete Product',
            message: 'Delete this product?',
            confirmLabel: 'Delete',
            onConfirm: () => deleteProduct(id),
        });
    };

    const handleConfirm = async () => {
        const action = pendingConfirm?.onConfirm;
        if (pendingConfirm?.exportFilters && exportBeforeDelete) {
            if (!await exportAccounts(pendingConfirm.exportFilters)) return;
        }
        setPendingConfirm(null);
        if (action) await action();
    };

    const statusBadge = (s) => {
        const map = { active: 'badge-success', unused: 'badge-info', expired: 'badge-warning', banned: 'badge-danger' };
        return <span className={`badge ${map[s] || 'badge-info'}`}>{s}</span>;
    };
    const fmt = (s) => (s ? new Date(String(s).replace(' ', 'T')).toLocaleDateString() : '—');

    return (
        <div>
            {pendingConfirm && (
                <div className="system-confirm-overlay" onClick={() => setPendingConfirm(null)}>
                    <div className="system-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="system-confirm-title" onClick={(e) => e.stopPropagation()}>
                        <div className="system-confirm-icon"><Icon name="alert" size={18} /></div>
                        <div className="system-confirm-body">
                            <h3 id="system-confirm-title">{pendingConfirm.title}</h3>
                            <p>{pendingConfirm.message}</p>
                            {pendingConfirm.exportFilters && <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                                <input type="checkbox" checked={exportBeforeDelete} onChange={e => setExportBeforeDelete(e.target.checked)} disabled={exportingAccounts} />
                                Download Excel backup before deleting
                            </label>}
                        </div>
                        <div className="system-confirm-actions">
                            {pendingConfirm.exportFilters && <button className="btn btn-secondary" disabled={exportingAccounts} onClick={() => exportAccounts(pendingConfirm.exportFilters)}>{exportingAccounts ? 'Exporting…' : 'Export Excel only'}</button>}
                            <button className="btn btn-secondary" disabled={exportingAccounts} onClick={() => setPendingConfirm(null)}>Cancel</button>
                            <button className="btn btn-danger" disabled={exportingAccounts} onClick={handleConfirm}>{exportingAccounts ? 'Exporting…' : pendingConfirm.exportFilters && exportBeforeDelete ? 'Export & delete' : pendingConfirm.confirmLabel || 'Confirm'}</button>
                        </div>
                    </div>
                </div>
            )}

            {stats && (
                <div className="stats-grid">
                    <div className="stat-card"><div className="stat-label">Total Keys</div><div className="stat-value">{stats.totalCards}</div></div>
                    <div className="stat-card" style={{ borderLeftColor: '#1b8a5a' }}><div className="stat-label">Active Keys</div><div className="stat-value">{stats.activeCards}</div></div>
                    <div className="stat-card" style={{ borderLeftColor: '#0b7da6' }}><div className="stat-label">Accounts in Stock</div><div className="stat-value">{stats.stockTotal}</div></div>
                    <div className="stat-card" style={{ borderLeftColor: '#ffa239' }}><div className="stat-label">Available Now</div><div className="stat-value">{stats.stockAvailable}</div></div>
                </div>
            )}

            {notice && <div className="alert alert-success" style={{ marginBottom: 16 }}><span><Icon name="check" size={16} /></span><div>{notice}</div></div>}
            {error && <div className="alert alert-error" style={{ marginBottom: 16 }}><span><Icon name="alert" size={16} /></span><div>{error}</div></div>}

            <div className="redeem-admin-subtabs">
                {[['cards', 'Keys'], ['accounts', 'Stock Accounts'], ['products', 'Products'], ['tutorial', 'Operation Tutorial']].map(([id, label]) => (
                    <button key={id} className={`btn ${sub === id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSub(id)}>{label}</button>
                ))}
            </div>

            {sub === 'cards' && (
                <>
                    <div className="card">
                        <div className="card-header"><div className="card-title">Generate Keys</div></div>
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Product</label>
                                <select className="form-input" value={gen.productName} onChange={(e) => setGen({ ...gen, productName: e.target.value })}>
                                    <option value="">-- Select product --</option>
                                    {productNames.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Duration</label>
                                <select className="form-input" value={gen.durationMonths} onChange={(e) => setGen({ ...gen, durationMonths: Number(e.target.value) })}>
                                    {DURATION_OPTIONS.map((m) => (
                                        <option key={m} value={m}>{m < 0 ? `${-m} day${m === -1 ? '' : 's'}` : `${m} month${m > 1 ? 's' : ''}`}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Quantity</label>
                                <input className="form-input" type="number" min="1" max="500" value={gen.count} onChange={(e) => setGen({ ...gen, count: e.target.value })} />
                            </div>
                        </div>
                        <button className="btn btn-primary" onClick={generate}><Icon name="plus" size={15} /> Generate</button>

                        {generatedKeys && (
                            <div style={{ marginTop: 16 }}>
                                <div className="form-label">New keys ({generatedKeys.length}) — copy these now:</div>
                                <textarea className="form-input" readOnly rows={Math.min(8, generatedKeys.length)} value={generatedKeys.join('\n')} onFocus={(e) => e.target.select()} style={{ fontFamily: 'monospace' }} />
                            </div>
                        )}
                    </div>

                    <div className="card">
                        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div className="card-title">Keys</div>
                            {(invalidCounts.banned + invalidCounts.expired) > 0 && (
                                <button className="btn btn-danger" onClick={deleteInvalid}
                                    title="Deletes every banned and expired key, in one go">
                                    <Icon name="trash" size={14} /> Delete all invalid ({invalidCounts.banned + invalidCounts.expired})
                                </button>
                            )}
                        </div>
                        <div className="filters-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                            <select className="form-input" style={{ maxWidth: 160 }} value={cardFilters.status} onChange={(e) => { setCardPage(1); setCardFilters({ ...cardFilters, status: e.target.value }); }}>
                                {['all', 'unused', 'active', 'expired', 'banned'].map((s) => <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>)}
                            </select>
                            <select className="form-input" style={{ maxWidth: 180 }} value={cardFilters.product} onChange={(e) => { setCardPage(1); setCardFilters({ ...cardFilters, product: e.target.value }); }}>
                                <option value="all">All products</option>
                                {productNames.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <input className="form-input" style={{ maxWidth: 240 }} placeholder="Search key or email…" value={cardFilters.keyword} onChange={(e) => setCardFilters({ ...cardFilters, keyword: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { setCardPage(1); loadCards(); } }} />
                            <button className="btn btn-secondary" disabled={cards.length === 0 || exportingAccounts} onClick={exportKeyAccounts}
                                title="Excel of the accounts (email, password, mailbox login) behind every key in this filter, all pages">
                                {exportingAccounts ? 'Exporting…' : 'Export accounts (Excel)'}
                            </button>
                        </div>

                        <table className="data-table">
                            <thead><tr><th>Key</th><th>Product</th><th>Duration</th><th>Owner</th><th>Status</th><th>Bound Email</th><th>Activated</th><th>Expires</th><th>Actions</th></tr></thead>
                            <tbody>
                                {cards.length === 0 ? (
                                    <tr><td colSpan={9}><div className="empty-state"><div className="empty-text">No keys yet</div></div></td></tr>
                                ) : cards.map((c) => (
                                    <tr key={c.key}>
                                        <td><code>{c.key}</code></td>
                                        <td>{c.productName}</td>
                                        <td>{c.durationMonths ? durationLabel(c.durationMonths) : '—'}</td>
                                        <td>{c.resellerUsername || 'Admin'}</td>
                                        <td>{statusBadge(c.status)}</td>
                                        <td>
                                            {c.accountEmail || (c.lastAccountEmail ? (
                                                <span title="Account this key used before it expired. It has been retired (marked invalid) so it is never given to another buyer.">
                                                    <span style={{ color: '#64748b' }}>{c.lastAccountEmail}</span>
                                                    <span style={{ display: 'block', fontSize: 11, color: '#b45309' }}>retired · was used by this key</span>
                                                </span>
                                            ) : '—')}
                                        </td>
                                        <td>{fmt(c.activateTime)}</td>
                                        <td>{fmt(c.expireTime)}</td>
                                        <td>
                                            <div className="table-actions">
                                                {c.status === 'banned'
                                                    ? <button className="btn btn-secondary" onClick={() => cardAction(c.key, 'unban')}>Unban</button>
                                                    : <button className="btn btn-secondary" onClick={() => cardAction(c.key, 'ban')}>Ban</button>}
                                                <button className="btn btn-danger" onClick={() => cardAction(c.key, 'delete')} aria-label="Delete"><Icon name="trash" size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="pagination" style={{ marginTop: 16 }}>
                            <button className="btn btn-secondary" disabled={cardPage <= 1} onClick={() => setCardPage(1)}>« First</button>
                            <button className="btn btn-secondary" disabled={cardPage <= 1} onClick={() => setCardPage((p) => p - 1)}>‹ Prev</button>
                            <span style={{ padding: '0 12px', alignSelf: 'center' }}>Page {cardPage} / {cardPages}</span>
                            <button className="btn btn-secondary" disabled={cardPage >= cardPages} onClick={() => setCardPage((p) => p + 1)}>Next ›</button>
                            <button className="btn btn-secondary" disabled={cardPage >= cardPages} onClick={() => setCardPage(cardPages)}>Last »</button>
                        </div>
                    </div>
                </>
            )}

            {sub === 'accounts' && (
                <>
                    <div className="card">
                        <div className="card-header"><div className="card-title">Add Accounts to Stock</div></div>
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Product</label>
                                <select className="form-input" value={addAcc.productName} onChange={(e) => setAddAcc({ ...addAcc, productName: e.target.value })}>
                                    <option value="">-- Select product --</option>
                                    {productNames.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                        </div>
                        <p className="empty-text" style={{ textAlign: 'left', marginBottom: 12 }}>
                            Stock is shared — any account can fill a key of any duration (the key carries the duration).
                            Paste <code>email----password----clientId----refreshToken</code> to also register the Outlook
                            OTP mailbox, so codes are fetched from that inbox automatically. The customer only ever
                            receives the email &amp; password — never the token. <code>email:password</code> still works for
                            accounts that don't need OTP.
                        </p>
                        <div className="form-group">
                            <label className="form-label">Accounts (one per line, <code>email----password----clientId----refreshToken</code>)</label>
                            <textarea className="form-input" rows={6} placeholder={'user1@outlook.com----pass1----9e5f94bc-…----M.C…refreshtoken\nuser2@outlook.com----pass2----9e5f94bc-…----M.C…refreshtoken'} value={addAcc.accounts} onChange={(e) => setAddAcc({ ...addAcc, accounts: e.target.value })} style={{ fontFamily: 'monospace' }} />
                        </div>
                        <div className="form-group" style={{ maxWidth: 520 }}>
                            <label className="form-label" htmlFor="import-account-notes">Notes / organization</label>
                            <input id="import-account-notes" className="form-input" maxLength={500} placeholder="e.g. Name 1" value={addAcc.notes} onChange={(e) => setAddAcc({ ...addAcc, notes: e.target.value })} />
                            <div className="empty-text" style={{ textAlign: 'left', marginTop: 6 }}>Applies to every new account in this import. Use the same label to manage an organization's accounts together.</div>
                        </div>
                        <button className="btn btn-primary" onClick={addAccounts}><Icon name="plus" size={15} /> Add to stock</button>

                        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--app-border)' }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={allowSwitchWithoutMailbox}
                                    onChange={(e) => saveSwitchSettings(e.target.checked)}
                                    style={{ marginTop: 3 }}
                                />
                                <span>
                                    <strong>Allow "Change account" to use accounts without a working mailbox</strong>
                                    <div className="empty-text" style={{ textAlign: 'left', marginTop: 4 }}>
                                        Customers switching accounts are always given one with a working OTP mailbox first.
                                        {allowSwitchWithoutMailbox
                                            ? ' Because this is ON, if none is free they will still get an account — but they may not be able to fetch a verification code from it.'
                                            : ' Because this is OFF, if none is free the switch is refused with "no replacement account available" rather than handing out an account that can’t receive codes.'}
                                    </div>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header"><div className="card-title">Stock Accounts</div></div>
                        {stats && (() => {
                            // Clickable status summary — click a card to filter the list below.
                            // "In Use" is the live-demand number for planning how many
                            // licenses to prepare next time.
                            const cards = [
                                { key: 'all', label: 'Total', value: stats.stockTotal, color: '#334155', bg: '#f1f5f9', border: '#e2e8f0' },
                                { key: 'inuse', label: 'In Use', value: stats.stockInUse ?? 0, color: '#92400e', bg: '#fffbeb', border: '#fde68a' },
                                { key: 'available', label: 'Available', value: stats.stockAvailable, color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
                                { key: 'invalid', label: 'Invalid', value: stats.stockInvalid ?? 0, color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
                            ];
                            return (
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                                    {cards.map((c) => {
                                        const activeFilter = accFilters.status === c.key;
                                        return (
                                            <button
                                                key={c.key}
                                                type="button"
                                                onClick={() => { setAccPage(1); setAccFilters({ ...accFilters, status: c.key }); }}
                                                title={`Show ${c.label.toLowerCase()} accounts`}
                                                style={{
                                                    flex: '1 1 120px', minWidth: 120, textAlign: 'left', cursor: 'pointer',
                                                    padding: '12px 14px', borderRadius: 10,
                                                    background: c.bg, border: `1px solid ${c.border}`,
                                                    outline: activeFilter ? `2px solid ${c.color}` : 'none',
                                                    outlineOffset: 1,
                                                }}
                                            >
                                                <div style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1.1 }}>{c.value.toLocaleString()}</div>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: c.color, opacity: 0.85 }}>{c.label}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                        <div className="filters-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                            <select className="form-input" style={{ maxWidth: 160 }} value={accFilters.status} onChange={(e) => { setAccPage(1); setAccFilters({ ...accFilters, status: e.target.value }); }}>
                                <option value="all">All statuses</option>
                                <option value="inuse">In Use</option>
                                <option value="available">Available</option>
                                <option value="invalid">Invalid</option>
                            </select>
                            <select className="form-input" style={{ maxWidth: 180 }} value={accFilters.product} onChange={(e) => { setAccPage(1); setAccFilters({ ...accFilters, product: e.target.value }); }}>
                                <option value="all">All products</option>
                                {productNames.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <input className="form-input" style={{ maxWidth: 240 }} placeholder="Search email / domain…" value={accFilters.keyword} onChange={(e) => setAccFilters({ ...accFilters, keyword: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { setAccPage(1); loadAccounts(); } }} />
                            <select aria-label="Filter by notes or organization" className="form-input" style={{ maxWidth: 220 }} value={accFilters.notes} onChange={(e) => { setAccPage(1); setAccFilters({ ...accFilters, notes: e.target.value }); }}>
                                <option value="">All notes / organizations</option>
                                {accFilters.notes && !noteLabels.includes(accFilters.notes) && <option value={accFilters.notes}>{accFilters.notes}</option>}
                                {noteLabels.map(note => <option key={note} value={note}>{note}</option>)}
                            </select>
                            <button className="btn btn-secondary" onClick={() => { setAccPage(1); loadAccounts(); }}>Search</button>
                            <button className="btn btn-secondary" disabled={!accountTotal || exportingAccounts} onClick={() => exportAccounts({ ...accFilters })}>{exportingAccounts ? 'Exporting…' : 'Export matching Excel'}</button>
                            <button
                                className="btn btn-danger"
                                onClick={invalidateSearch}
                                disabled={!accountTotal || !(accFilters.notes || accFilters.keyword.trim() || accFilters.product !== 'all' || accFilters.status !== 'all')}
                                title="Mark every account matching the current filter invalid; their keys auto-heal onto new accounts"
                            >
                                <Icon name="alert" size={14} /> Invalidate matching
                            </button>
                            <button className="btn btn-danger" onClick={deleteMatchingAccounts} disabled={!accountTotal || !(accFilters.notes || accFilters.keyword.trim() || accFilters.product !== 'all' || accFilters.status !== 'all')}>
                                <Icon name="trash" size={14} /> Delete matching
                            </button>
                            {invalidAccounts.deletable > 0 && (
                                <button
                                    className="btn btn-danger"
                                    onClick={deleteInvalidAccounts}
                                    title="Deletes every invalid account in one go (those still used by a customer's live key are kept)"
                                >
                                    <Icon name="trash" size={14} /> Delete all invalid ({invalidAccounts.deletable.toLocaleString()})
                                </button>
                            )}
                        </div>
                        <p className="empty-text" style={{ textAlign: 'left', marginTop: -6, marginBottom: 14 }}>
                            {accountTotal.toLocaleString()} matching accounts. Select an organization's notes (e.g. <code>Name 1</code>) to invalidate or delete that group across all pages. Notes labels match exactly. Customers of invalidated accounts can use "Account abnormal? Change account" to switch.
                        </p>

                        <table className="data-table">
                            <thead><tr><th>Email</th><th>Notes / organization</th><th>Product</th><th>Status</th><th>OTP mailbox</th><th>Used</th><th>Actions</th></tr></thead>
                            <tbody>
                                {accounts.length === 0 ? (
                                    <tr><td colSpan={7}><div className="empty-state"><div className="empty-text">No accounts in stock</div></div></td></tr>
                                ) : accounts.map((a) => (
                                    <tr key={a.id}>
                                        <td><code>{a.email}</code></td>
                                        <td style={{ minWidth: 150, maxWidth: 260, overflowWrap: 'anywhere' }}>
                                            {notesEditor?.id === a.id ? <>
                                                <input aria-label={`Notes for ${a.email}`} className="form-input" maxLength={500} value={notesEditor.notes} onChange={e => setNotesEditor({ ...notesEditor, notes: e.target.value })} />
                                                <div className="table-actions" style={{ marginTop: 6 }}>
                                                    <button className="btn btn-primary" onClick={saveAccountNotes}>Save</button>
                                                    <button className="btn btn-secondary" onClick={() => setNotesEditor(null)}>Cancel</button>
                                                </div>
                                            </> : <>
                                                <span>{a.notes || '—'}</span>
                                                <button className="btn btn-secondary" style={{ marginLeft: 6, padding: '2px 6px', fontSize: 11 }} onClick={() => setNotesEditor({ id: a.id, notes: a.notes || '' })}>Edit</button>
                                            </>}
                                        </td>
                                        <td>{a.productName}</td>
                                        <td>{a.disabled ? <span className="badge badge-danger">Invalid</span> : a.isUsed ? <span className="badge badge-warning">In use</span> : <span className="badge badge-success">Available</span>}</td>
                                        <td>
                                            {!a.mailboxId && a.mailboxProvider !== 'custom'
                                                ? <span className="badge" style={{ background: '#f1f5f9', color: '#64748b' }} title="No OTP mailbox linked — re-stock with the 4-part format to enable code fetching">None</span>
                                                : a.mailboxProvider === 'custom'
                                                    ? <span className="badge badge-success">Custom</span>
                                                : a.mailboxDisabled
                                                    ? <span className="badge badge-danger" title={a.mailboxError || 'Mailbox disabled'}>Dead</span>
                                                    : a.mailboxError
                                                        ? <span className="badge badge-warning" title={a.mailboxError}>Warning</span>
                                                        : <span className="badge badge-success" title={a.mailboxLastUsed ? `Last used ${fmt(a.mailboxLastUsed)}` : 'Token linked'}>Linked</span>}
                                            {a.mailboxId || a.mailboxProvider === 'custom' ? (
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => testAccountMailbox(a.id)}
                                                    disabled={otpTest[a.id]?.status === 'loading'}
                                                    title="Test mailbox connectivity (refresh token + open inbox)"
                                                    style={{ padding: '2px 6px', marginLeft: 6, fontSize: 11 }}
                                                >
                                                    {otpTest[a.id]?.status === 'loading' ? '…' : 'Test'}
                                                </button>
                                            ) : null}
                                            {otpTest[a.id] && otpTest[a.id].status !== 'loading' && (
                                                <div style={{ marginTop: 4, fontSize: 11, color: otpTest[a.id].status === 'success' ? '#15803d' : '#f87171' }}>
                                                    {otpTest[a.id].message}
                                                </div>
                                            )}
                                        </td>
                                        <td>{a.usageCount}</td>
                                        <td>
                                            <div className="table-actions">
                                                {a.disabled
                                                    ? <button className="btn btn-secondary" onClick={() => accountAction(a.id, 'reenable')}>Re-enable</button>
                                                    : <button className="btn btn-secondary" onClick={() => accountAction(a.id, 'invalidate')}>Invalidate</button>}
                                                {!a.disabled && a.isUsed ? <button className="btn btn-secondary" onClick={() => accountAction(a.id, 'release')}>Release</button> : null}
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => handleOtpButtonClick(a.id)}
                                                    disabled={otpLookup[a.id]?.status === 'loading'}
                                                    style={{ padding: '6px 8px' }}
                                                >
                                                    <Icon name="mail" size={14} />
                                                </button>
                                                <button className="btn btn-danger" onClick={() => accountAction(a.id, 'delete')} aria-label="Delete"><Icon name="trash" size={14} /></button>
                                            </div>
                                            {otpLookup[a.id] && otpLookup[a.id].status !== 'loading' && (
                                                <div style={{ marginTop: 6, fontSize: 12 }}>
                                                    {otpLookup[a.id].status === 'success' ? (
                                                        <code
                                                            title="Click to copy"
                                                            onClick={() => { navigator.clipboard.writeText(otpLookup[a.id].code); flash('Code copied!'); }}
                                                            style={{ cursor: 'pointer', background: 'rgba(148,163,184,0.15)', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}
                                                        >
                                                            {otpLookup[a.id].code}
                                                        </code>
                                                    ) : (
                                                        <span style={{ color: '#f87171' }}>{otpLookup[a.id].message}</span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="pagination" style={{ marginTop: 16 }}>
                            <button className="btn btn-secondary" disabled={accPage <= 1} onClick={() => setAccPage(1)}>« First</button>
                            <button className="btn btn-secondary" disabled={accPage <= 1} onClick={() => setAccPage((p) => p - 1)}>‹ Prev</button>
                            <span style={{ padding: '0 12px', alignSelf: 'center' }}>Page {accPage} / {accPages}</span>
                            <button className="btn btn-secondary" disabled={accPage >= accPages} onClick={() => setAccPage((p) => p + 1)}>Next ›</button>
                            <button className="btn btn-secondary" disabled={accPage >= accPages} onClick={() => setAccPage(accPages)}>Last »</button>
                        </div>
                    </div>
                </>
            )}

            {sub === 'products' && (
                <div className="card">
                    <div className="card-header"><div className="card-title">Products</div></div>
                    <p className="empty-text" style={{ marginBottom: 16, textAlign: 'left' }}>
                        A product is an account pool, for example "Adobe". Add accounts for it in Stock Accounts, then
                        generate keys in the Keys tab. To sell those keys through Pre-Made Keys, copy the generated keys
                        and import them in Pre-Made Keys Management.
                    </p>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                        <input className="form-input" style={{ maxWidth: 280 }} placeholder="New product name (e.g. Adobe)" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') addProduct(); }} />
                        <button className="btn btn-primary" onClick={addProduct}><Icon name="plus" size={15} /> Add product</button>
                    </div>

                    <table className="data-table">
                        <thead><tr><th>Name</th><th>OTP Lookup</th><th>Sender Keywords</th><th>Actions</th></tr></thead>
                        <tbody>
                            {products.length === 0 ? (
                                <tr><td colSpan={4}><div className="empty-state"><div className="empty-text">No products defined</div></div></td></tr>
                            ) : products.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.name}</td>
                                    <td>{p.otpEnabled !== false ? <span className="badge badge-success">Enabled</span> : <span className="badge badge-warning">Disabled</span>}</td>
                                    <td>{p.otpSenderKeywords || p.otpDefaults?.senderKeywords || 'Generic'}</td>
                                    <td>
                                        <div className="table-actions">
                                            <button className="btn btn-secondary" onClick={() => openOtpProduct(p)}><Icon name="settings" size={14} /> Configure OTP</button>
                                            <button className="btn btn-danger" onClick={() => confirmDeleteProduct(p.id)} aria-label="Delete"><Icon name="trash" size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {otpProduct && (
                        <div className="card" style={{ marginTop: 18 }}>
                            <div className="card-header">
                                <div>
                                    <div className="card-title">OTP Rules: {otpProduct.name}</div>
                                    <p className="empty-text" style={{ textAlign: 'left', marginTop: 4 }}>
                                        Match the sender and subject, then extract the code with the first capture group in each pattern.
                                    </p>
                                </div>
                            </div>

                            <label className="checkbox-row" style={{ marginBottom: 14 }}>
                                <input
                                    type="checkbox"
                                    checked={!!otpForm.otpEnabled}
                                    onChange={(e) => setOtpForm({ ...otpForm, otpEnabled: e.target.checked })}
                                />
                                Enable Get Code for this product
                            </label>

                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">Sender keywords</label>
                                    <textarea
                                        className="form-input"
                                        rows={4}
                                        placeholder="anydesk"
                                        value={otpForm.otpSenderKeywords}
                                        onChange={(e) => setOtpForm({ ...otpForm, otpSenderKeywords: e.target.value })}
                                    />
                                    <p className="empty-text" style={{ textAlign: 'left', marginTop: 6 }}>One per line or comma separated. Leave blank to use product defaults.</p>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Subject keywords</label>
                                    <textarea
                                        className="form-input"
                                        rows={4}
                                        placeholder="verification code"
                                        value={otpForm.otpSubjectKeywords}
                                        onChange={(e) => setOtpForm({ ...otpForm, otpSubjectKeywords: e.target.value })}
                                    />
                                    <p className="empty-text" style={{ textAlign: 'left', marginTop: 6 }}>Examples: security code, login code, OTP, passcode.</p>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Code patterns</label>
                                <textarea
                                    className="form-input"
                                    rows={5}
                                    value={otpForm.otpCodePatterns}
                                    onChange={(e) => setOtpForm({ ...otpForm, otpCodePatterns: e.target.value })}
                                    style={{ fontFamily: 'monospace' }}
                                />
                                <p className="empty-text" style={{ textAlign: 'left', marginTop: 6 }}>
                                    Regex, one per line. The first capture group is returned as the code.
                                </p>
                            </div>

                            <div className="form-group" style={{ maxWidth: 220 }}>
                                <label className="form-label">Fresh window (minutes)</label>
                                <input
                                    className="form-input"
                                    type="number"
                                    min="1"
                                    max="60"
                                    value={otpForm.otpFreshMinutes}
                                    onChange={(e) => setOtpForm({ ...otpForm, otpFreshMinutes: e.target.value })}
                                />
                            </div>

                            <div className="table-actions" style={{ marginTop: 12 }}>
                                <button className="btn btn-primary" onClick={() => saveProduct()}><Icon name="save" size={15} /> Save OTP rules</button>
                                <button className="btn btn-secondary" onClick={useOtpDefaults}>Use defaults</button>
                                <button className="btn btn-secondary" onClick={() => setOtpProduct(null)}>Close</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {sub === 'tutorial' && (
                <div className="card">
                    <div className="card-header"><div className="card-title">Operation Tutorial (Redemption Page)</div></div>
                    <p className="empty-text" style={{ marginBottom: 16, textAlign: 'left' }}>
                        This text shows in the yellow box on the customer redemption page. Write <strong>one step per line</strong> — each line becomes a numbered point.
                        Pick a <strong>product</strong> to give it its own tutorial, or <strong>Default</strong> for the fallback used by any product without one.
                        A product with an empty tutorial falls back to Default, then to the page's built-in text.
                    </p>
                    <div className="form-group" style={{ maxWidth: 360 }}>
                        <label className="form-label">Tutorial for</label>
                        <select className="form-input" value={tutorialTarget} onChange={(e) => selectTutorialTarget(e.target.value)}>
                            <option value="__default__">Default (fallback for all products)</option>
                            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Tutorial steps (one per line)</label>
                        <textarea
                            className="form-input"
                            rows={8}
                            placeholder={"We provide one active email address only. Complete the registration yourself...\nIf your account becomes unavailable, visit our website and click \"Account Abnormal? Change Account\"..."}
                            value={tutorialDraft}
                            onChange={(e) => setTutorialDraft(e.target.value)}
                        />
                    </div>
                    {tutorialDraft.trim() && (
                        <div style={{ marginBottom: 16 }}>
                            <div className="form-label" style={{ marginBottom: 6 }}>Preview</div>
                            <div className="redeem-note" style={{ maxWidth: 520 }}>
                                <strong className="redeem-note-title">Operation Tutorial:</strong>
                                <ol className="redeem-note-list">
                                    {tutorialDraft.split(/\r?\n/).map(s => s.trim()).filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
                                </ol>
                            </div>
                        </div>
                    )}
                    <button className="btn btn-primary" onClick={saveTutorial}><Icon name="save" size={15} /> Save tutorial</button>
                </div>
            )}

            {sub === 'pricing' && (
                <div className="card">
                    <div className="card-header"><div className="card-title">Global Key Pricing (Credits)</div></div>
                    <p className="empty-text" style={{ marginBottom: 16, textAlign: 'left' }}>
                        Set the credit price per duration. This applies to all resellers buying keys. Leave a month blank
                        to fall back to each reseller's own per-month config (then to the month number itself).
                    </p>
                    <div className="redeem-pricing-grid">
                        {DURATION_OPTIONS.map((m) => (
                            <div className="form-group" key={m}>
                                <label className="form-label">{m < 0 ? `${-m} Day${m === -1 ? '' : 's'}` : `${m} Month${m > 1 ? 's' : ''}`}</label>
                                <input
                                    className="form-input"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder={m < 0 ? (m === -1 ? 'e.g. 0.1' : m === -7 ? 'e.g. 0.4' : 'e.g. 0.6') : `e.g. ${m}`}
                                    value={pricingDraft[m] ?? pricingDraft[String(m)] ?? ''}
                                    onChange={(e) => setPricingDraft({ ...pricingDraft, [m]: e.target.value })}
                                />
                            </div>
                        ))}
                    </div>
                    <button className="btn btn-primary" onClick={savePricing}><Icon name="save" size={15} /> Save pricing</button>
                    {Object.keys(pricing).length > 0 && (
                        <p className="empty-text" style={{ marginTop: 12, textAlign: 'left' }}>
                            Currently priced: {Object.keys(pricing).filter(m => pricing[m] != null && pricing[m] !== '').map(m => `${durationLabel(m)}=${pricing[m]}`).join(', ') || 'none'}.
                        </p>
                    )}
                </div>
            )}

            {sub === 'settings' && (
                <div className="card">
                    <div className="card-header"><div className="card-title">Redemption Page</div></div>

                    <div className="form-group">
                        <label className="form-label">Redeem URL</label>
                        <input
                            className="form-input"
                            type="text"
                            placeholder="https://your-domain.com/redeem-key"
                            value={settings.redeemUrl}
                            onChange={(e) => setSettings({ ...settings, redeemUrl: e.target.value })}
                        />
                        <p className="empty-text" style={{ marginTop: 6, textAlign: 'left' }}>
                            The public page where customers redeem their key. Resellers see this link so they know where to send buyers.
                            Your built-in redemption page lives at <code>/redeem-key</code> — set the full URL (including domain) here.
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Reseller-Facing Description</label>
                        <textarea
                            className="form-input"
                            rows={4}
                            placeholder="Shown to resellers in Activate User > Get Pre-Made Key (e.g. what the key includes, how customers redeem it)…"
                            value={settings.description}
                            onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                        />
                        <p className="empty-text" style={{ marginTop: 6, textAlign: 'left' }}>
                            This message appears to resellers in Activate User > Get Pre-Made Key.
                        </p>
                    </div>

                    <button className="btn btn-primary" onClick={saveSettings}><Icon name="save" size={15} /> Save settings</button>
                </div>
            )}
        </div>
    );
}

export default KeyRedemptionAdmin;
